import { useEffect, useRef, useState } from 'react';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import type { LDContext } from 'launchdarkly-js-client-sdk';

import { LD_APPLICATION_CONTEXT } from 'src/constants/launch-darkly';
import { markLdIdentifiedDispensary } from 'src/utils/launch-darkly/identified-dispensary';
import { Session, useSessionResetWatcher } from 'utils/session';

/** Lets the identify calls already in flight settle before we correct the context. */
export const KIOSK_REIDENTIFY_DELAY_MS = 1500;
export const KIOSK_REIDENTIFY_MAX_ATTEMPTS = 2;

// Attempts for the current session and store only. Module scope survives remounts. A
// Session.reset() or a store change replaces the entry and starts a new budget, so a stale
// identify for the previous store that lands last is still corrected for the current one.
let budget: { key: string; attempts: number } = { key: '', attempts: 0 };
const attemptsFor = (key: string): number => (budget.key === key ? budget.attempts : 0);

export function resetKioskReidentifyAttemptsForTests(): void {
  budget = { key: '', attempts: 0 };
}

type KioskDispensary =
  | {
      id?: string | null;
      retailer?: { enterpriseId?: string | null } | null;
      /** Includes the enterprise Concierge AI setting and the store's kiosk toggle. Missing/null fails closed. */
      conciergeChannels?: { kiosk?: boolean | null } | null;
    }
  | null
  | undefined;

/**
 * Kiosk only, and only for stores with `conciergeChannels.kiosk` on. LD identify
 * responses are unordered, so one without the `ecomm-dispensary` context can land
 * last and leave dispensary-targeted flags false. Once the dispensary is loaded and
 * LD's context lacks it (or has another store), identify again with it. Bounded
 * per session and store, so it never loops. Does nothing when `window.reactEnv.dispensaryId`
 * was present at boot (the bootstrapper worker seeded the context, so there is no race).
 */
export function useKioskLdDispensaryReidentify(dispensary: KioskDispensary, isKiosk: boolean): void {
  const client = useLDClient();
  // Re-render when identify replaces the flag set, so the context is re-read.
  useFlags();
  useSessionResetWatcher();
  // Read on every render (the watcher re-renders on reset), so a new session re-runs the effect.
  const sessionId = Session.getInstance().data.id;
  // LD emits 'change' only when a flag value changes, so an identify that swaps the store
  // but not the flags re-renders nothing. Re-check after our own identify settles instead.
  const [recheck, setRecheck] = useState(0);
  const latestDispensaryIdRef = useRef<string | undefined>(undefined);

  const dispensaryId = dispensary?.id ?? undefined;
  latestDispensaryIdRef.current = dispensaryId;
  const enterpriseId = dispensary?.retailer?.enterpriseId ?? undefined;
  // LDClient types getContext() as non-null, but it returns null before the first identify.
  const context = client?.getContext() as LDContext | null | undefined;
  const contextDispensaryId = (context as Record<string, { key?: string } | undefined> | null | undefined)?.[
    'ecomm-dispensary'
  ]?.key;
  // Only stores with the kiosk concierge channel on (strict). Not gated on the LD
  // flag: that flag is the value this race makes wrong.
  const isStoreEnabled = dispensary?.conciergeChannels?.kiosk === true;
  const needsReidentify =
    isKiosk &&
    isStoreEnabled &&
    Boolean(client) &&
    Boolean(context) &&
    Boolean(dispensaryId) &&
    contextDispensaryId !== dispensaryId &&
    !(typeof window !== 'undefined' && window.reactEnv?.dispensaryId);

  useEffect(() => {
    if (!needsReidentify || !client || !dispensaryId) {
      return undefined;
    }
    const budgetKey = `${sessionId}:${dispensaryId}`;
    if (attemptsFor(budgetKey) >= KIOSK_REIDENTIFY_MAX_ATTEMPTS) {
      return undefined;
    }

    const timerId = setTimeout(() => {
      const attempts = attemptsFor(budgetKey);
      // Read the context now: an in-flight identify may have fixed it during the delay.
      const current = client.getContext() as Record<string, unknown> | null;
      const currentDispensary = current?.['ecomm-dispensary'] as { key?: string } | undefined;
      if (attempts >= KIOSK_REIDENTIFY_MAX_ATTEMPTS || !current || currentDispensary?.key === dispensaryId) {
        return;
      }
      budget = { key: budgetKey, attempts: attempts + 1 };

      const fullContext = {
        ...current,
        kind: 'multi',
        application: LD_APPLICATION_CONTEXT,
        'ecomm-dispensary': { key: dispensaryId },
        ...(enterpriseId ? { 'ecomm-enterprise': { key: enterpriseId } } : {}),
      } as LDContext;

      client
        .identify(fullContext)
        .then(() => {
          // A stale identify may have landed after ours; re-render to re-read the context.
          const landed = (client.getContext() as Record<string, { key?: string } | undefined> | null)?.[
            'ecomm-dispensary'
          ]?.key;
          markLdIdentifiedDispensary(landed);
          if (landed !== latestDispensaryIdRef.current) {
            setRecheck((value) => value + 1);
          }
        })
        .catch((error: unknown) => {
          console.error('Error re-identifying kiosk in LaunchDarkly:', error);
        });
    }, KIOSK_REIDENTIFY_DELAY_MS);

    return () => clearTimeout(timerId);
  }, [needsReidentify, client, contextDispensaryId, dispensaryId, enterpriseId, sessionId, recheck]);
}
